import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Divider,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabaseClient';

const DashboardPage = () => {
  const { currentUser } = useAuth();
  const [funis, setFunis] = useState([]);
  const [funilId, setFunilId] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('me');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [deals, setDeals] = useState([]); // negociações em memória (por enquanto apenas criadas nesta sessão)

  // Estado do modal de criação de negociação
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [nomeNegociacao, setNomeNegociacao] = useState('');
  const [etapaId, setEtapaId] = useState('');
  const [fonteId, setFonteId] = useState('');
  const [empresaId, setEmpresaId] = useState('');
  const [fontes, setFontes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loadingLookups, setLoadingLookups] = useState(false);

  // Detalhes da negociação
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailTab, setDetailTab] = useState('historico');
  const [detailNome, setDetailNome] = useState('');
  const [detailEtapaId, setDetailEtapaId] = useState('');
  const [detailFonteId, setDetailFonteId] = useState('');
  const [detailEmpresaId, setDetailEmpresaId] = useState('');
  const [savingDetail, setSavingDetail] = useState(false);

  // Tarefas da negociação
  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDueDate, setNewTaskDueDate] = useState('');
  const [newTaskDescription, setNewTaskDescription] = useState('');
  const [editingTaskId, setEditingTaskId] = useState('');
  const [editTaskTitle, setEditTaskTitle] = useState('');
  const [editTaskDescription, setEditTaskDescription] = useState('');
  const [editTaskDueDate, setEditTaskDueDate] = useState('');
  const [savingTask, setSavingTask] = useState(false);

  // Histórico da negociação
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const carregarFunis = async () => {
    try {
      setLoading(true);

      const { data: funisData, error: funisError } = await supabase
        .from('funis')
        .select('id, nome')
        .order('nome', { ascending: true });

      if (funisError) throw funisError;

      const { data: etapasData, error: etapasError } = await supabase
        .from('funis_etapas')
        .select('id, funil_id, nome, sigla, ordem')
        .order('ordem', { ascending: true });

      if (etapasError) throw etapasError;

      const funisComEtapas = (funisData || []).map((funil) => ({
        ...funil,
        etapas: (etapasData || []).filter((e) => e.funil_id === funil.id),
      }));

      setFunis(funisComEtapas);

      setFunilId((prev) => {
        if (prev) return prev;
        return funisComEtapas[0]?.id || '';
      });
    } catch (error) {
      console.error('Erro ao carregar funis no dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarHistorico = async (negociacaoId) => {
    if (!negociacaoId) return;

    try {
      setLoadingHistory(true);
      const { data, error } = await supabase
        .from('negociacoes_historico')
        .select('*')
        .eq('negociacao_id', negociacaoId)
        .order('criado_em', { ascending: false });

      if (error) throw error;

      setHistory(data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const registrarEventoHistorico = async (negociacaoId, tipo, descricao) => {
    if (!negociacaoId) return;
    try {
      const { data, error } = await supabase
        .from('negociacoes_historico')
        .insert({ negociacao_id: negociacaoId, tipo, descricao })
        .select('*')
        .single();

      if (error) throw error;

      setHistory((prev) => [data, ...prev]);
    } catch (error) {
      console.error('Erro ao registrar histórico:', error);
    }
  };

  useEffect(() => {
    carregarFunis();
  }, []);

  const carregarNegociacoes = useCallback(
    async (funilIdParam) => {
      const alvoFunilId = funilIdParam || funilId;
      if (!alvoFunilId) return;

      try {
        const { data, error } = await supabase
          .from('negociacoes')
          .select('*')
          .eq('funil_id', alvoFunilId)
          .order('criado_em', { ascending: false });

        if (error) throw error;

        setDeals(data || []);
      } catch (error) {
        console.error('Erro ao carregar negociações:', error);
      }
    },
    [funilId],
  );

  // Recarrega negociações sempre que o funil selecionado mudar
  useEffect(() => {
    if (funilId) {
      carregarNegociacoes(funilId);
    }
  }, [funilId, carregarNegociacoes]);

  const funilAtual = useMemo(
    () => funis.find((f) => f.id === funilId) || funis[0],
    [funilId, funis]
  );

  const dealsByStage = useMemo(() => {
    const result = {};
    if (!funilAtual) return result;

    funilAtual.etapas.forEach((etapa) => {
      result[etapa.id] = [];
    });

    deals
      .filter((deal) => deal.funil_id === funilAtual.id)
      .forEach((deal) => {
        const key = deal.etapa_id;
        if (!result[key]) result[key] = [];
        result[key].push(deal);
      });

    return result;
  }, [funilAtual, deals]);

  const carregarFontesEEmpresas = async () => {
    try {
      setLoadingLookups(true);

      const [{ data: fontesData, error: fontesError }, { data: empresasData, error: empresasError }] =
        await Promise.all([
          supabase.from('fontes').select('id, nome').order('nome', { ascending: true }),
          supabase.from('empresas').select('id, nome').order('nome', { ascending: true }),
        ]);

      if (fontesError) throw fontesError;
      if (empresasError) throw empresasError;

      setFontes(fontesData || []);
      setEmpresas(empresasData || []);
    } catch (error) {
      console.error('Erro ao carregar fontes/empresas:', error);
    } finally {
      setLoadingLookups(false);
    }
  };

  const abrirModalCriar = () => {
    if (!funilAtual) return;

    setNomeNegociacao('');
    setEtapaId(funilAtual.etapas[0]?.id || '');
    setFonteId('');
    setEmpresaId('');
    setCreateOpen(true);
    carregarFontesEEmpresas();
  };

  const fecharModalCriar = () => {
    if (creating) return;
    setCreateOpen(false);
  };

  const handleCriarNegociacao = async () => {
    if (!nomeNegociacao.trim() || !funilAtual || !etapaId) return;

    try {
      setCreating(true);

      const payload = {
        titulo: nomeNegociacao.trim(),
        funil_id: funilAtual.id,
        etapa_id: etapaId,
        fonte_id: fonteId || null,
        empresa_id: empresaId || null,
      };

      const { data, error } = await supabase
        .from('negociacoes')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;

      setDeals((prev) => [...prev, data]);
      setCreateOpen(false);

      // Histórico: criação de negociação
      registrarEventoHistorico(data.id, 'criacao', 'Negociação criada');
    } catch (error) {
      console.error('Erro ao criar negociação:', error);
    } finally {
      setCreating(false);
    }
  };

  const abrirDetalheNegociacao = (deal) => {
    setSelectedDeal(deal);
    setDetailTab('historico');
    setDetailNome(deal.titulo || '');
    setDetailEtapaId(deal.etapa_id || '');
    setDetailFonteId(deal.fonte_id || '');
    setDetailEmpresaId(deal.empresa_id || '');
    setDetailOpen(true);

    // Garante que temos fontes/empresas carregadas para mostrar os nomes
    if (fontes.length === 0 || empresas.length === 0) {
      carregarFontesEEmpresas();
    }
  };

  const fecharDetalheNegociacao = () => {
    setDetailOpen(false);
  };

  const funilDaNegociacao = useMemo(() => {
    if (!selectedDeal) return null;
    return funis.find((f) => f.id === selectedDeal.funil_id) || null;
  }, [selectedDeal, funis]);

  const etapaDaNegociacao = useMemo(() => {
    if (!selectedDeal || !funilDaNegociacao) return null;
    const alvoId = detailEtapaId || selectedDeal.etapa_id;
    return funilDaNegociacao.etapas.find((e) => e.id === alvoId) || null;
  }, [selectedDeal, funilDaNegociacao, detailEtapaId]);

  // Os detalhes de fonte/empresa da negociação ainda não são exibidos diretamente na UI.

  const carregarTarefas = async (negociacaoId) => {
    if (!negociacaoId) return;

    try {
      setLoadingTasks(true);
      const { data, error } = await supabase
        .from('tarefas_negociacao')
        .select('*')
        .eq('negociacao_id', negociacaoId)
        .order('data_vencimento', { ascending: true })
        .order('criado_em', { ascending: true });

      if (error) throw error;

      setTasks(data || []);
    } catch (error) {
      console.error('Erro ao carregar tarefas:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  // Sempre que abrirmos o detalhe de uma negociação, carregamos as tarefas
  useEffect(() => {
    if (detailOpen && selectedDeal) {
      carregarTarefas(selectedDeal.id);
      carregarHistorico(selectedDeal.id);
    }
  }, [detailOpen, selectedDeal]);

  const handleSalvarDetalhes = async () => {
    if (!selectedDeal || !funilDaNegociacao) return;

    try {
      setSavingDetail(true);

      const etapaAnteriorId = selectedDeal.etapa_id;

      const payload = {
        titulo: detailNome.trim() || selectedDeal.titulo,
        etapa_id: detailEtapaId || selectedDeal.etapa_id,
        fonte_id: detailFonteId || null,
        empresa_id: detailEmpresaId || null,
      };

      const { data, error } = await supabase
        .from('negociacoes')
        .update(payload)
        .eq('id', selectedDeal.id)
        .select('*')
        .single();

      if (error) throw error;

      setSelectedDeal(data);
      setDeals((prev) => prev.map((d) => (d.id === data.id ? data : d)));
      // Recarrega negociações do funil para garantir que o board esteja consistente
      carregarNegociacoes(funilDaNegociacao.id);
      // Fecha o modal após salvar
      setDetailOpen(false);

      // Histórico: mudança de etapa, se houver
      if (payload.etapa_id !== etapaAnteriorId && funilDaNegociacao) {
        const etapaAnterior = funilDaNegociacao.etapas.find((e) => e.id === etapaAnteriorId);
        const etapaNova = funilDaNegociacao.etapas.find((e) => e.id === payload.etapa_id);
        const descricao = `Etapa alterada de "${etapaAnterior?.nome || 'N/A'}" para "${
          etapaNova?.nome || 'N/A'
        }"`;
        registrarEventoHistorico(data.id, 'mudanca_etapa', descricao);
      }
    } catch (error) {
      console.error('Erro ao salvar detalhes da negociação:', error);
    } finally {
      setSavingDetail(false);
    }
  };

  const handleCriarTarefa = async () => {
    if (!selectedDeal || !newTaskTitle.trim()) return;

    try {
      setLoadingTasks(true);
      const payload = {
        negociacao_id: selectedDeal.id,
        titulo: newTaskTitle.trim(),
        descricao: newTaskDescription.trim() || null,
        data_vencimento: newTaskDueDate || null,
      };

      const { data, error } = await supabase
        .from('tarefas_negociacao')
        .insert(payload)
        .select('*')
        .single();

      if (error) throw error;

      setTasks((prev) => [...prev, data]);
      setNewTaskTitle('');
      setNewTaskDueDate('');
      setNewTaskDescription('');

      // Atualiza indicador de tarefas pendentes e histórico
      await atualizarIndicadorTarefas(selectedDeal.id);
      registrarEventoHistorico(
        selectedDeal.id,
        'tarefa_criada',
        `Tarefa criada: "${data.titulo}"`,
      );
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleToggleTarefa = async (task) => {
    try {
      const isCompleted = task.status === 'concluida';
      const payload = {
        status: isCompleted ? 'pendente' : 'concluida',
        concluido_em: isCompleted ? null : new Date().toISOString(),
      };

      const { data, error } = await supabase
        .from('tarefas_negociacao')
        .update(payload)
        .eq('id', task.id)
        .select('*')
        .single();

      if (error) throw error;

      setTasks((prev) => prev.map((t) => (t.id === data.id ? data : t)));

      if (selectedDeal) {
        await atualizarIndicadorTarefas(selectedDeal.id);
        registrarEventoHistorico(
          selectedDeal.id,
          isCompleted ? 'tarefa_reaberta' : 'tarefa_concluida',
          `${isCompleted ? 'Tarefa reaberta' : 'Tarefa concluída'}: "${data.titulo}"`,
        );
      }
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  const handleEditarTarefa = (task) => {
    setEditingTaskId(task.id);
    setEditTaskTitle(task.titulo || '');
    setEditTaskDescription(task.descricao || '');
    setEditTaskDueDate(task.data_vencimento || '');
  };

  const handleCancelarEdicaoTarefa = () => {
    setEditingTaskId('');
    setEditTaskTitle('');
    setEditTaskDescription('');
    setEditTaskDueDate('');
  };

  const handleSalvarEdicaoTarefa = async () => {
    if (!editingTaskId || !editTaskTitle.trim()) return;

    try {
      setSavingTask(true);

      const payload = {
        titulo: editTaskTitle.trim(),
        descricao: editTaskDescription.trim() || null,
        data_vencimento: editTaskDueDate || null,
      };

      const { data, error } = await supabase
        .from('tarefas_negociacao')
        .update(payload)
        .eq('id', editingTaskId)
        .select('*')
        .single();

      if (error) throw error;

      setTasks((prev) => prev.map((t) => (t.id === data.id ? data : t)));
      handleCancelarEdicaoTarefa();
      if (selectedDeal) {
        registrarEventoHistorico(
          selectedDeal.id,
          'tarefa_editada',
          `Tarefa editada: "${data.titulo}"`,
        );
      }
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
    } finally {
      setSavingTask(false);
    }
  };

  const handleExcluirTarefa = async (taskId) => {
    if (!window.confirm('Deseja realmente excluir esta tarefa?')) return;

    try {
      const { error } = await supabase
        .from('tarefas_negociacao')
        .delete()
        .eq('id', taskId);

      if (error) throw error;

      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      if (editingTaskId === taskId) {
        handleCancelarEdicaoTarefa();
      }

      if (selectedDeal) {
        await atualizarIndicadorTarefas(selectedDeal.id);
        registrarEventoHistorico(selectedDeal.id, 'tarefa_excluida', 'Tarefa excluída');
      }
    } catch (error) {
      console.error('Erro ao excluir tarefa:', error);
    }
  };

  const atualizarIndicadorTarefas = async (negociacaoId) => {
    try {
      const { count, error } = await supabase
        .from('tarefas_negociacao')
        .select('*', { count: 'exact', head: true })
        .eq('negociacao_id', negociacaoId)
        .eq('status', 'pendente');

      if (error) throw error;

      const temPendente = (count || 0) > 0;

      const { data: updated, error: updError } = await supabase
        .from('negociacoes')
        .update({ tem_tarefa_pendente: temPendente })
        .eq('id', negociacaoId)
        .select('*')
        .single();

      if (updError) throw updError;

      setDeals((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
      if (selectedDeal && selectedDeal.id === negociacaoId) {
        setSelectedDeal(updated);
      }
    } catch (error) {
      console.error('Erro ao atualizar indicador de tarefas:', error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Cabeçalho */}
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Olá, {currentUser?.nome || 'Usuário'}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bem-vindo ao seu CRM Hadritech. Acompanhe suas negociações por funil de vendas.
        </Typography>
      </Box>

      {/* Filtros principais */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette.background.paper,
        }}
      >
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="funil-label">Funil de vendas</InputLabel>
          <Select
            labelId="funil-label"
            value={funilAtual?.id || ''}
            label="Funil de vendas"
            onChange={(e) => setFunilId(e.target.value)}
            disabled={loading || funis.length === 0}
          >
            {funis.map((f) => (
              <MenuItem key={f.id} value={f.id}>
                {f.nome}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="owner-label">Proprietário</InputLabel>
          <Select
            labelId="owner-label"
            value={ownerFilter}
            label="Proprietário"
            onChange={(e) => setOwnerFilter(e.target.value)}
          >
            <MenuItem value="me">Minhas negociações</MenuItem>
            <MenuItem value="all">Todas as negociações</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">Todos os status</MenuItem>
            <MenuItem value="open">Abertas</MenuItem>
            <MenuItem value="won">Ganhas</MenuItem>
            <MenuItem value="lost">Perdidas</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Atualizar funil">
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              carregarFunis();
              if (funilAtual) carregarNegociacoes(funilAtual.id);
            }}
            disabled={loading}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{ textTransform: 'none' }}
          onClick={abrirModalCriar}
          disabled={!funilAtual}
        >
          Criar negociação
        </Button>
      </Paper>

      {/* Colunas do funil */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 1,
        }}
      >
        {funilAtual?.etapas.map((etapa) => {
          const deals = dealsByStage[etapa.id] || [];
          return (
            <Paper
              key={etapa.id}
              sx={{
                minWidth: 260,
                maxWidth: 320,
                flexShrink: 0,
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
                backgroundColor: (theme) => theme.palette.background.paper,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '70vh',
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 600, flexGrow: 1 }}
                  noWrap
                >
                  {etapa.nome}
                </Typography>
                <Chip
                  label={etapa.sigla}
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Box
                sx={{
                  px: 1.5,
                  pt: 1,
                  pb: 1.5,
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {deals.length} negociações
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  R$ 0,00
                </Typography>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  overflowY: 'auto',
                }}
              >
                {deals.length === 0 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontStyle: 'italic' }}
                  >
                    Nenhuma negociação nesta etapa.
                  </Typography>
                ) : (
                  deals.map((deal) => (
                    <Paper
                      key={deal.id}
                      variant="outlined"
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        cursor: 'pointer',
                        '&:hover': {
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => abrirDetalheNegociacao(deal)}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                        noWrap
                      >
                        {deal.titulo}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {deal.contato}
                      </Typography>
                      <Box
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          R$ {deal.valor?.toLocaleString('pt-BR') || '0,00'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {deal.previsaoFechamento}
                        </Typography>
                      </Box>
                      {deal.tem_tarefa_pendente && (
                        <Typography
                          variant="caption"
                          color="primary"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          Tarefas pendentes
                        </Typography>
                      )}
                    </Paper>
                  ))
                )}
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Modal de criação de negociação */}
      <Dialog open={createOpen} onClose={fecharModalCriar} fullWidth maxWidth="sm">
        <DialogTitle>Criar negociação</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nome da negociação"
            fullWidth
            value={nomeNegociacao}
            onChange={(e) => setNomeNegociacao(e.target.value)}
            autoFocus
            required
          />

          <FormControl fullWidth size="small">
            <InputLabel id="etapa-negociacao-label">Etapa do funil</InputLabel>
            <Select
              labelId="etapa-negociacao-label"
              value={etapaId}
              label="Etapa do funil"
              onChange={(e) => setEtapaId(e.target.value)}
            >
              {funilAtual?.etapas.map((etapa) => (
                <MenuItem key={etapa.id} value={etapa.id}>
                  {etapa.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="fonte-negociacao-label">Fonte</InputLabel>
            <Select
              labelId="fonte-negociacao-label"
              value={fonteId}
              label="Fonte"
              onChange={(e) => setFonteId(e.target.value)}
              disabled={loadingLookups}
            >
              <MenuItem value="">
                <em>Sem fonte</em>
              </MenuItem>
              {fontes.map((fonte) => (
                <MenuItem key={fonte.id} value={fonte.id}>
                  {fonte.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="empresa-negociacao-label">Empresa da negociação</InputLabel>
            <Select
              labelId="empresa-negociacao-label"
              value={empresaId}
              label="Empresa da negociação"
              onChange={(e) => setEmpresaId(e.target.value)}
              disabled={loadingLookups}
            >
              <MenuItem value="">
                <em>Sem empresa</em>
              </MenuItem>
              {empresas.map((empresa) => (
                <MenuItem key={empresa.id} value={empresa.id}>
                  {empresa.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharModalCriar} disabled={creating}>
            Cancelar
          </Button>
          <Button
            onClick={handleCriarNegociacao}
            variant="contained"
            disabled={creating || !nomeNegociacao.trim() || !etapaId}
          >
            Criar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detalhes da negociação */}
      <Dialog
        open={detailOpen}
        onClose={fecharDetalheNegociacao}
        fullWidth
        maxWidth="xl"
      >
        <DialogTitle>
          {selectedDeal?.titulo || 'Negociação'}
        </DialogTitle>
        <DialogContent sx={{ mt: 1 }}>
          {/* Faixa de etapas do funil, similar ao RD */}
          {funilDaNegociacao && (
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                overflowX: 'auto',
                p: 1,
                borderRadius: 2,
                backgroundColor: (theme) => theme.palette.background.default,
              }}
            >
              {funilDaNegociacao.etapas.map((etapa) => {
                const isCurrent = etapa.id === (detailEtapaId || selectedDeal?.etapa_id);
                return (
                  <Box
                    key={etapa.id}
                    sx={{
                      px: 2,
                      py: 1,
                      mr: 1,
                      borderRadius: 999,
                      whiteSpace: 'nowrap',
                      fontSize: 13,
                      fontWeight: isCurrent ? 700 : 500,
                      color: isCurrent
                        ? '#ffffff'
                        : (theme) => theme.palette.text.primary,
                      backgroundColor: (theme) =>
                        isCurrent
                          ? theme.palette.primary.main
                          : theme.palette.primary.light + '33',
                      border: (theme) =>
                        isCurrent
                          ? `1px solid ${theme.palette.primary.main}`
                          : `1px solid ${theme.palette.primary.light}`,
                      cursor: 'pointer',
                      '&:hover': {
                        opacity: 0.9,
                      },
                    }}
                    onClick={() => setDetailEtapaId(etapa.id)}
                  >
                    {etapa.nome}
                  </Box>
                );
              })}
            </Box>
          )}

          <Grid container spacing={3}>
            {/* Coluna esquerda: resumo da negociação */}
            <Grid item xs={12} md={4}>
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, backgroundColor: (theme) => theme.palette.background.paper }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Negociação
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Nome
                </Typography>
                <TextField
                  size="small"
                  fullWidth
                  value={detailNome}
                  onChange={(e) => setDetailNome(e.target.value)}
                  sx={{ mb: 1 }}
                />

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Criada em
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {selectedDeal?.criado_em
                    ? new Date(selectedDeal.criado_em).toLocaleString('pt-BR')
                    : '-'}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Funil
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {funilDaNegociacao?.nome || '-'}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Etapa
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {etapaDaNegociacao?.nome || '-'}
                </Typography>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Fonte
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select
                    value={detailFonteId}
                    onChange={(e) => setDetailFonteId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Sem fonte</em>
                    </MenuItem>
                    {fontes.map((fonte) => (
                      <MenuItem key={fonte.id} value={fonte.id}>
                        {fonte.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Empresa
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <Select
                    value={detailEmpresaId}
                    onChange={(e) => setDetailEmpresaId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>Sem empresa vinculada</em>
                    </MenuItem>
                    {empresas.map((empresa) => (
                      <MenuItem key={empresa.id} value={empresa.id}>
                        {empresa.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Responsável
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {currentUser?.nome || 'Usuário'}
                </Typography>
              </Paper>
            </Grid>

            {/* Coluna direita: abas (Histórico, E-mail, Tarefas, etc.) */}
            <Grid item xs={12} md={8}>
              <Paper
                variant="outlined"
                sx={{ p: 0, minHeight: 320, display: 'flex', flexDirection: 'column' }}
              >
                <Tabs
                  value={detailTab}
                  onChange={(_, value) => setDetailTab(value)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
                >
                  <Tab label="Tarefas" value="tarefas" />
                  <Tab label="Produtos e Serviços" value="produtos" />
                  <Tab label="Arquivos" value="arquivos" />
                  <Tab label="Propostas" value="propostas" />
                  <Tab label="Pagamentos" value="pagamentos" />
                  <Tab label="Histórico" value="historico" />
                </Tabs>

                <Box sx={{ p: 2, flexGrow: 1 }}>
                  {detailTab === 'historico' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {loadingHistory ? (
                        <Typography variant="body2" color="text.secondary">
                          Carregando histórico...
                        </Typography>
                      ) : history.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Ainda não há eventos registrados para esta negociação.
                        </Typography>
                      ) : (
                        history.map((item) => (
                          <Box
                            key={item.id}
                            sx={{
                              p: 1,
                              borderRadius: 1,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              backgroundColor: (theme) => theme.palette.background.paper,
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {item.criado_em
                                ? new Date(item.criado_em).toLocaleString('pt-BR')
                                : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 0.25 }}>
                              {item.descricao}
                            </Typography>
                          </Box>
                        ))
                      )}
                    </Box>
                  )}
                  {detailTab === 'tarefas' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <TextField
                            label="Título da tarefa"
                            size="small"
                            value={newTaskTitle}
                            onChange={(e) => setNewTaskTitle(e.target.value)}
                            sx={{ flexGrow: 1, minWidth: 220 }}
                          />
                          <TextField
                            label="Vencimento"
                            size="small"
                            type="date"
                            InputLabelProps={{ shrink: true }}
                            value={newTaskDueDate}
                            onChange={(e) => setNewTaskDueDate(e.target.value)}
                            sx={{ width: 160 }}
                          />
                        </Box>
                        <TextField
                          label="Descrição da tarefa (opcional)"
                          size="small"
                          multiline
                          minRows={2}
                          value={newTaskDescription}
                          onChange={(e) => setNewTaskDescription(e.target.value)}
                        />
                        <Box>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleCriarTarefa}
                            disabled={!newTaskTitle.trim() || loadingTasks}
                            sx={{ textTransform: 'none' }}
                          >
                            Adicionar tarefa
                          </Button>
                        </Box>
                      </Box>

                      <Divider />

                      {loadingTasks ? (
                        <Typography variant="body2" color="text.secondary">
                          Carregando tarefas...
                        </Typography>
                      ) : tasks.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          Não há tarefas cadastradas para esta negociação.
                        </Typography>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {tasks.map((task) => {
                            const isCompleted = task.status === 'concluida';
                            const isEditing = editingTaskId === task.id;
                            return (
                              <Box
                                key={task.id}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  gap: 1,
                                  p: 1.25,
                                  borderRadius: 1,
                                  border: (theme) => `1px solid ${theme.palette.divider}`,
                                  backgroundColor: (theme) =>
                                    isCompleted
                                      ? theme.palette.action.hover
                                      : theme.palette.background.paper,
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={isCompleted}
                                  onChange={() => handleToggleTarefa(task)}
                                  sx={{ mt: isEditing ? 2.5 : 0.5 }}
                                />
                                <Box sx={{ flexGrow: 1 }}>
                                  {isEditing ? (
                                    <>
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Título"
                                        value={editTaskTitle}
                                        onChange={(e) => setEditTaskTitle(e.target.value)}
                                        sx={{ mb: 1 }}
                                      />
                                      <TextField
                                        size="small"
                                        fullWidth
                                        label="Descrição"
                                        multiline
                                        minRows={2}
                                        value={editTaskDescription}
                                        onChange={(e) => setEditTaskDescription(e.target.value)}
                                        sx={{ mb: 1 }}
                                      />
                                      <TextField
                                        size="small"
                                        label="Vencimento"
                                        type="date"
                                        InputLabelProps={{ shrink: true }}
                                        value={editTaskDueDate}
                                        onChange={(e) => setEditTaskDueDate(e.target.value)}
                                        sx={{ mb: 1 }}
                                      />
                                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={handleSalvarEdicaoTarefa}
                                          disabled={savingTask || !editTaskTitle.trim()}
                                          sx={{ textTransform: 'none' }}
                                        >
                                          Salvar
                                        </Button>
                                        <Button
                                          size="small"
                                          onClick={handleCancelarEdicaoTarefa}
                                          disabled={savingTask}
                                          sx={{ textTransform: 'none' }}
                                        >
                                          Cancelar
                                        </Button>
                                      </Box>
                                    </>
                                  ) : (
                                    <>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          textDecoration: isCompleted ? 'line-through' : 'none',
                                          fontWeight: 600,
                                        }}
                                      >
                                        {task.titulo}
                                      </Typography>
                                      {task.descricao && (
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                          sx={{
                                            mt: 0.25,
                                            textDecoration: isCompleted
                                              ? 'line-through'
                                              : 'none',
                                          }}
                                        >
                                          {task.descricao}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mt: 0.5 }}
                                      >
                                        {task.data_vencimento
                                          ? `Vencimento: ${new Date(
                                              task.data_vencimento,
                                            ).toLocaleDateString('pt-BR')}`
                                          : 'Sem data de vencimento'}
                                        {task.criado_em
                                          ? ` • Criada em: ${new Date(
                                              task.criado_em,
                                            ).toLocaleString('pt-BR')}`
                                          : ''}
                                        {task.concluido_em
                                          ? ` • Concluída em: ${new Date(
                                              task.concluido_em,
                                            ).toLocaleString('pt-BR')}`
                                          : ''}
                                      </Typography>
                                    </>
                                  )}
                                </Box>
                                {!isEditing && (
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditarTarefa(task)}
                                      sx={{ mb: 0.5 }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleExcluirTarefa(task.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                    </Box>
                  )}
                  {detailTab === 'produtos' && (
                    <Typography variant="body2" color="text.secondary">
                      Em breve: associe produtos e serviços a esta negociação.
                    </Typography>
                  )}
                  {detailTab === 'arquivos' && (
                    <Typography variant="body2" color="text.secondary">
                      Em breve: faça upload de arquivos e documentos importantes.
                    </Typography>
                  )}
                  {detailTab === 'propostas' && (
                    <Typography variant="body2" color="text.secondary">
                      Em breve: gere e acompanhe propostas comerciais desta negociação.
                    </Typography>
                  )}
                  {detailTab === 'pagamentos' && (
                    <Typography variant="body2" color="text.secondary">
                      Em breve: acompanhe pagamentos e condições comerciais ligados a esta negociação.
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharDetalheNegociacao} disabled={savingDetail}>
            Fechar
          </Button>
          <Button
            onClick={handleSalvarDetalhes}
            variant="contained"
            disabled={savingDetail || !selectedDeal}
          >
            Salvar alterações
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardPage;
